import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { forkJoin, of, switchMap, tap } from 'rxjs';
import { Auth } from './auth.service';
import { LoginPrompt } from './login-prompt.service';
import { CartModel, CartItemModel } from '../../shared/models/cart-item';
import { environment } from '../../../environments/environment.dev';

@Injectable({
  providedIn: 'root',
})
export class Carts {

  private apiUrl = `${environment.apiUrl}/cart`;

  isOpen = signal<boolean>(false);
  cartItems = signal<CartItemModel[]>([]);
  loginPulse = signal(false);

  totalItems = computed(() => this.cartItems().reduce((acc, item) => acc + item.quantity, 0));
  subtotal = computed(() => this.cartItems().reduce((acc, item) => acc + (item.productPrice * item.quantity), 0));

  constructor(
    private http: HttpClient,
    private auth: Auth,
    private loginPrompt: LoginPrompt
  ) {
    if (this.auth.isLoggedIn()) {
      this.refreshCart();
    }
  }

  toggleCart() {
    this.isOpen.update(v => !v);
  }

  clearCart() {
    this.cartItems.set([]);
  }

  clearCartAfterPurchase() {
    this.cartItems.set([]);
    const headers = this.getHeaders();
    if (!headers) return;

    this.http.get<CartModel>(`${this.apiUrl}/my-cart`, { headers })
      .pipe(switchMap((cartData) => {
        const ids = (cartData?.cartItems || []).map(item => item.idCartItem).filter(id => !!id);
        return ids.length
          ? forkJoin(ids.map(id => this.http.delete(`${this.apiUrl}/remove/${id}`, { headers })))
          : of([]);
      }))
      .subscribe({
        next: () => this.cartItems.set([]),
        error: (err) => console.error('Error al vaciar carrito', err)
      });
  }

  refreshCart() {
    const headers = this.getHeaders();
    if (!headers) return;

    this.http.get<CartModel>(`${this.apiUrl}/my-cart`, { headers })
      .subscribe({
        next: (cartData) => {
          if (cartData && cartData.cartItems) {
            const mappedItems = cartData.cartItems.map(item => ({
              ...item,
              selectedSize: item.size,
              selectedColor: item.color,
              subTotal: item.productPrice * item.quantity
            }));

            this.cartItems.set(mappedItems);
          } else {
            this.cartItems.set([]);
          }
        },
        error: (err) => console.error('Error cargando carrito', err)
      });
  }

  addToCart(productId: number, quantity: number = 1, size?: string, color?: string, price?: number) {
    if (!this.auth.isLoggedIn()) {
      this.pulseLoginBadge();
      this.loginPrompt.require('Inicia sesion para agregar productos al carrito');
      return;
    }

    const headers = this.getHeaders();
    if (!headers) {
      this.pulseLoginBadge();
      this.loginPrompt.require('Inicia sesion para agregar productos al carrito');
      return;
    }

    const formData = new FormData();
    formData.append('idProduct', productId.toString());
    formData.append('quantity', quantity.toString());

    if (size) formData.append('size', size);
    if (color) formData.append('color', color);

    if (price) formData.append('price', price.toString());

    this.http.post(`${this.apiUrl}/add`, formData, { headers })
      .pipe(tap(() => {
        this.refreshCart();
        this.isOpen.set(true);
      }))
      .subscribe({
        error: (e) => {
          console.error('Error al anadir', e);
          if (e.status === 403 || e.status === 401) {
            this.pulseLoginBadge();
            this.loginPrompt.require('Tu sesion ha expirado. Inicia sesion de nuevo.');
          }
        }
      });
  }

  removeFromCart(idCartItem: number) {
    const headers = this.getHeaders();
    if (!headers) return;

    this.http.delete(`${this.apiUrl}/remove/${idCartItem}`, { headers })
      .pipe(tap(() => this.refreshCart()))
      .subscribe({
        error: (err) => console.error('Error al eliminar item', err)
      });
  }

  updateQuantity(idCartItem: number, size: string | undefined, newQuantity: number) {
    if (newQuantity < 1) return;

    const headers = this.getHeaders();
    if (!headers) return;

    const params = new HttpParams()
      .set('idCartItem', idCartItem)
      .set('quantity', newQuantity);

    this.http.put(`${this.apiUrl}/update`, null, { headers, params })
      .pipe(tap(() => this.refreshCart()))
      .subscribe({
        next: () => {
          this.cartItems.update(items =>
            items.map(item => item.idCartItem === idCartItem ? {
              ...item,
              quantity: newQuantity,
              subTotal: item.productPrice * newQuantity
            } : item)
          );
        },
        error: (err) => console.error('Error actualizando cantidad', err)
      });
  }

  requireLoginForCheckout() {
    this.pulseLoginBadge();
    this.loginPrompt.require('Inicia sesion para procesar tu compra');
  }

  private pulseLoginBadge() {
    this.loginPulse.set(true);
    setTimeout(() => this.loginPulse.set(false), 2500);
  }

  private getHeaders(): HttpHeaders | null {
    const token = this.auth.getToken();
    if (!token) {
      return null;
    }
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });
  }
}
